import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createTacticalGoal } from '../../services/hoshinService';
import type { RoleType } from '../../services/scopeService';
import type { OrgMember } from '../../services/organizationService';
import type { HoshinSelection } from './HoshinObjectivesTable';
import {
  formatHoshinDate,
  isHoshinDatePast,
  MiniProgress,
  SectionTitle,
  WorkStatus,
  workTone,
} from './hoshinShared';

interface HoshinCockpitPanelProps {
  selection: HoshinSelection | null;
  roleType: RoleType;
  members: OrgMember[];
  departments: Array<{ id: string; name: string }>;
  onRefresh: () => void;
}

const ACTION_STATUS: Record<string, string> = {
  pending: 'A fazer',
  todo: 'A fazer',
  in_progress: 'Em andamento',
  doing: 'Em andamento',
  completed: 'Concluída',
  done: 'Concluída',
  blocked: 'Bloqueada',
  cancelled: 'Cancelada',
  canceled: 'Cancelada',
};

export const HoshinCockpitPanel: React.FC<HoshinCockpitPanelProps> = ({
  selection,
  roleType,
  members,
  departments,
  onRefresh,
}) => {
  const navigate = useNavigate();

  if (!selection) {
    return (
      <div className="p-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Desdobramento</p>
        <p className="mt-2 text-[14px] font-semibold text-slate-900 dark:text-slate-100">Selecione um objetivo</p>
        <p className="mt-1 text-[12px] leading-relaxed text-slate-500">
          A meta do setor nasce aqui, com responsável, alvo e prazo.
        </p>
      </div>
    );
  }

  const isGoal = selection.kind === 'goal';
  const objective = isGoal ? selection.objective : selection.data;
  const goal = isGoal ? selection.data : null;
  const deadline = goal ? (goal.target_date ?? objective.target_date) : objective.target_date;
  const progress = goal ? goal.progress : objective.actualProgress;
  const actionCount = goal ? goal.action_count : objective.linkedActions.length;
  const tone = workTone(progress, actionCount, deadline);
  const actions = goal ? goal.linkedActions : objective.linkedActions;
  const canCascade = !isGoal && (roleType === 'manager' || roleType === 'director');
  const alvo = goal?.target_indicator?.trim();

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
          {isGoal ? 'Meta do setor' : 'Objetivo'}
        </p>
        {isGoal && (
          <p className="mt-1 truncate text-[11px] text-slate-500">{objective.title}</p>
        )}
        <p className="mt-1 text-[14px] font-semibold leading-snug text-slate-900 dark:text-slate-100">
          {isGoal ? goal?.title : objective.title}
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
          <Fact label={isGoal ? 'Responsável' : 'Sponsor'} value={isGoal ? displayName(goal?.ownerName) : displayName(objective.sponsorName)} />
          <Fact label="Setor" value={isGoal ? displayPlace(goal?.departmentName) : 'Diretoria'} />
          <Fact
            label="Prazo"
            value={formatHoshinDate(deadline)}
            warn={isHoshinDatePast(deadline) && progress < 100}
          />
          <Fact label="Ações" value={actionCount === 0 ? 'Nenhuma' : String(actionCount)} />
        </div>

        <div className="mt-3 rounded-md border border-slate-200 px-3 py-2 dark:border-slate-700">
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Alvo</div>
          <div className={`mt-0.5 text-[13px] ${alvo ? 'font-medium text-slate-900 dark:text-slate-100' : 'text-slate-400'}`}>
            {isGoal ? (alvo || 'Alvo não definido') : `${objective.goals.length} ${objective.goals.length === 1 ? 'meta de setor' : 'metas de setor'}`}
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Andamento das ações</div>
            <div className="mt-1">
              {actionCount === 0 ? <WorkStatus tone="empty" /> : <MiniProgress value={progress} />}
            </div>
          </div>
          {actionCount > 0 && <WorkStatus tone={tone} />}
        </div>

        {canCascade && (
          <TacticalGoalQuickForm
            objectiveId={objective.id}
            roleType={roleType}
            members={members}
            departments={departments}
            onCreated={onRefresh}
          />
        )}

        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <SectionTitle>Ações ligadas</SectionTitle>
            <button
              type="button"
              onClick={() => navigate('/acoes')}
              className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900 dark:text-slate-300"
            >
              <ExternalLink size={11} />
              Central de Ações
            </button>
          </div>
          {actions.length === 0 ? (
            <p className="text-[12px] leading-relaxed text-slate-500">
              Esta {isGoal ? 'meta' : 'árvore'} ainda não tem ação ligada.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {actions.map((action) => (
                <li key={action.id} className="flex items-start justify-between gap-3 py-1.5">
                  <span className="text-[12px] leading-snug text-slate-800 dark:text-slate-100">{action.title}</span>
                  <span className="shrink-0 text-[10px] text-slate-500">
                    {ACTION_STATUS[action.status] ?? action.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

function displayName(value: string | undefined): string {
  if (!value || value === '—') return 'Sem responsável';
  return value;
}

function displayPlace(value: string | undefined): string {
  if (!value || value === '—') return 'Sem setor';
  return value;
}

function Fact({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-0.5 text-[12px] font-medium ${warn ? 'text-red-600' : 'text-slate-800 dark:text-slate-100'}`}>
        {value}
      </div>
    </div>
  );
}

function TacticalGoalQuickForm({
  objectiveId,
  roleType,
  members,
  departments,
  onCreated,
}: {
  objectiveId: string;
  roleType: RoleType;
  members: OrgMember[];
  departments: Array<{ id: string; name: string }>;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [indicator, setIndicator] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fieldClass = 'w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-[12px] text-slate-800 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';

  const handleSave = async () => {
    if (!title.trim() || !assignedTo || !departmentId) {
      setError('Título, setor e responsável são obrigatórios.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createTacticalGoal({
        objective_id: objectiveId,
        department_id: departmentId,
        assigned_to: assignedTo,
        title: title.trim(),
        target_indicator: indicator.trim() || null,
        target_date: targetDate || null,
      }, roleType);
      setTitle('');
      setIndicator('');
      setTargetDate('');
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar a meta.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <div className="text-[11px] font-semibold text-slate-800 dark:text-slate-100">Nova meta do setor</div>
      <div className="mt-2 space-y-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="O que este setor vai entregar" className={fieldClass} />
        <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className={fieldClass}>
          <option value="">Setor</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className={fieldClass}>
          <option value="">Responsável</option>
          {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.user_name}</option>)}
        </select>
        <input value={indicator} onChange={(e) => setIndicator(e.target.value)} placeholder="Alvo, ex.: OTD 95%" className={fieldClass} />
        <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className={fieldClass} aria-label="Prazo" />
        {error && <p className="text-[11px] text-red-600">{error}</p>}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded bg-slate-800 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {saving ? 'Salvando…' : 'Desdobrar'}
        </button>
      </div>
    </div>
  );
}
